import { apiClient } from './apiClient';
import { DeviceTransfer, DeviceTransferList, TransferBox, TransferCreate, TransferCreated, TransferResolved, TransferStatus } from '../types/deviceTransfer';

/** The portability API: requests to take over gateways, and their answers. */
class DeviceTransferService {
  private get axios() {
    return apiClient.getAxiosInstance();
  }

  /** Who owns these gateways, who can answer, which templates may apply. */
  async resolve(deviceIds: string[], toClientId?: string): Promise<TransferResolved> {
    const res = await this.axios.post<TransferResolved>('/v2/device-transfers/resolve', { device_ids: deviceIds, to_client_id: toClientId });
    return res.data;
  }

  /** Opens one request per current owner of the gateways. */
  async create(body: TransferCreate): Promise<TransferCreated> {
    const res = await this.axios.post<TransferCreated>('/v2/device-transfers', body);
    return res.data;
  }

  async list(params: { box: TransferBox; status?: TransferStatus | ''; page: number; limit: number }, signal?: AbortSignal): Promise<DeviceTransferList> {
    const query: Record<string, string | number> = { box: params.box, page: params.page, limit: params.limit };
    if (params.status) query.status = params.status;
    const res = await this.axios.get<DeviceTransferList>('/v2/device-transfers', { params: query, signal });
    return res.data;
  }

  async get(id: number): Promise<DeviceTransfer> {
    const res = await this.axios.get<DeviceTransfer>(`/v2/device-transfers/${id}`);
    return res.data;
  }

  async accept(id: number, comment?: string): Promise<DeviceTransfer> {
    const res = await this.axios.post<DeviceTransfer>(`/v2/device-transfers/${id}/accept`, { comment: comment || null });
    return res.data;
  }

  async refuse(id: number, comment?: string): Promise<DeviceTransfer> {
    const res = await this.axios.post<DeviceTransfer>(`/v2/device-transfers/${id}/refuse`, { comment: comment || null });
    return res.data;
  }

  async cancel(id: number): Promise<DeviceTransfer> {
    const res = await this.axios.post<DeviceTransfer>(`/v2/device-transfers/${id}/cancel`);
    return res.data;
  }

  /** How many requests wait for the caller's answer. */
  async inboxCount(signal?: AbortSignal): Promise<number> {
    const res = await this.axios.get<{ pending: number }>('/v2/device-transfers/inbox/count', { signal });
    return res.data.pending;
  }
}

export const deviceTransferService = new DeviceTransferService();
